// src/components/Institution/Dashboard/MonthlySeries.js
import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Select } from "antd";
import { Line } from "@ant-design/plots";
import { useTranslation } from "react-i18next";

export function MonthlySeries({ timeseries = {}, loading }) {
  const { t } = useTranslation(["institutions", "common"]);

  const MONTHS = [
    t("common:months.jan"),
    t("common:months.feb"),
    t("common:months.mar"),
    t("common:months.apr"),
    t("common:months.may"),
    t("common:months.jun"),
    t("common:months.jul"),
    t("common:months.aug"),
    t("common:months.sep"),
    t("common:months.oct"),
    t("common:months.nov"),
    t("common:months.dec"),
  ];

  function normalizeArray(arr, name) {
    if (!Array.isArray(arr)) return [];
    return arr.map((p) => ({
      name,
      month: typeof p.month === "number" ? MONTHS[(p.month - 1) % 12] : p.month,
      value: Number(p.count ?? 0),
    }));
  }

  const years = useMemo(() => {
    return (timeseries.yearsIndex || []).filter((y) =>
      Number.isFinite(Number(y)),
    );
  }, [timeseries]);

  const [year, setYear] = useState(undefined);

  useEffect(() => {
    if (years.length > 0 && !year) {
      const current = new Date().getFullYear();
      setYear(years.includes(current) ? current : years[0]);
    }
  }, [years, year]);

  const dataSets = useMemo(() => {
    const getYearData = (key, label) => {
      const raw = timeseries?.[key]?.[year] || [];
      return normalizeArray(raw, label);
    };

    return [
      {
        key: "teachers",
        label: t("institutions:chart.teachersLabel"),
        data: getYearData("teachers", t("institutions:chart.teachersLabel")),
      },
      {
        key: "students",
        label: t("institutions:chart.studentsLabel"),
        data: getYearData("students", t("institutions:chart.studentsLabel")),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeseries, year, t]);

  const config = (data) => ({
    data,
    xField: "month",
    yField: "value",
    smooth: true,
    point: { size: 4, shape: "diamond" },
    height: 240,
    autoFit: true,
  });

  return (
    <>
      {years.length > 0 && (
        <div
          style={{
            margin: "8px 0 16px",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <span style={{ opacity: 0.7 }}>{t("partners:chart.year")}</span>
          <Select
            size="small"
            value={year}
            onChange={setYear}
            options={years.map((y) => ({ label: y, value: y }))}
            style={{ width: 100 }}
          />
        </div>
      )}

      <Row gutter={[16, 16]}>
        {dataSets.map((s) => (
          <Col key={s.key} xs={24} md={12}>
            <Card
              title={s.label}
              variant="outlined"
              hoverable
              style={{ borderRadius: 10 }}
              loading={loading}
            >
              <Line {...config(s.data)} />
            </Card>
          </Col>
        ))}
      </Row>
    </>
  );
}
